/**
 * AnnouncementThread — a calm modal that lists replies for an announcement and
 * lets the current user post a new reply.
 *
 * - `mode="broadcast"`: no reply input rendered.
 * - `mode="open"`: every reply is visible to every member of the target group.
 * - `mode="quiet"`: parents see only their own replies; staff sees all.
 *
 * Calm vocabulary, big spacing, soft pulse on new reply.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { X, Send, Lock, Users } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return new Date(iso).toLocaleDateString();
}

export default function AnnouncementThread({ announcement, onClose, currentUser }) {
  const [replies, setReplies] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);

  const mode = announcement.reply_mode || "broadcast";
  const canReply = mode !== "broadcast";

  const reload = async () => {
    if (mode === "broadcast") {
      setReplies([]);
      setLoading(false);
      return;
    }
    try {
      const r = await api.get(`/announcements/${announcement.id}/replies`);
      setReplies(r.data || []);
    } catch {
      setReplies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announcement.id]);

  useEffect(() => {
    if (endRef.current && replies.length > 0) {
      endRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [replies.length]);

  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post(`/announcements/${announcement.id}/replies`, {
        body: body.trim(),
      });
      setBody("");
      await reload();
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't send.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (replyId) => {
    try {
      await api.delete(
        `/announcements/${announcement.id}/replies/${replyId}`
      );
      await reload();
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't remove.");
    }
  };

  const modeBadge =
    mode === "open" ? (
      <span className="trust-pill" data-testid="thread-mode-open">
        <Users size={11} strokeWidth={1.5} />
        Open thread
      </span>
    ) : mode === "quiet" ? (
      <span
        className="trust-pill"
        style={{ background: "#F2E7CF", color: "#1A1B2E" }}
        data-testid="thread-mode-quiet"
      >
        <Lock size={11} strokeWidth={1.5} />
        Quiet · staff only
      </span>
    ) : (
      <span className="trust-pill" data-testid="thread-mode-broadcast">
        Broadcast · read only
      </span>
    );

  return (
    <div
      data-testid="thread-modal"
      className="fixed inset-0 z-[60] bg-[#FBF9F6]/95 backdrop-blur-xl flex items-center justify-center px-6 overflow-y-auto"
    >
      <div className="bg-white rounded-3xl border hairline p-7 max-w-xl w-full fade-up my-10 max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <span className="eyebrow">From the team</span>
            <h3 className="mt-2 font-serif text-2xl text-[#2C2B29] tracking-tight">
              {announcement.title}
            </h3>
            <p
              className="mt-2 text-sm text-[#6A6863] font-light"
              data-testid="thread-body"
            >
              {announcement.body}
            </p>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {modeBadge}
              {announcement.target_group_name && (
                <span className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863]">
                  {announcement.target_group_name}
                </span>
              )}
              <span className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863]">
                · {announcement.created_by_name}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="thread-close"
            aria-label="Close"
            className="text-[#6A6863] hover:text-[#2C2B29] p-2 -mt-1 -mr-1"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        {mode !== "broadcast" && (
          <div
            data-testid="thread-replies"
            className="mt-6 flex-1 overflow-y-auto space-y-3 pr-1"
          >
            {loading ? (
              <p className="font-serif italic text-[#6A6863]">A quiet moment…</p>
            ) : replies.length === 0 ? (
              <p
                data-testid="thread-empty"
                className="font-serif italic text-[#6A6863]"
              >
                {mode === "quiet"
                  ? "Nothing here yet — only staff will see your reply."
                  : "Nothing here yet — be the first to reply, gently."}
              </p>
            ) : (
              replies.map((r) => {
                const isMe = currentUser && r.author_id === currentUser.id;
                return (
                  <div
                    key={r.id}
                    data-testid={`thread-reply-${r.id}`}
                    className={
                      "rounded-2xl p-4 border hairline " +
                      (isMe ? "bg-[#132619]/5" : "bg-white")
                    }
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-serif text-sm text-[#2C2B29]">
                        {r.author_name}
                        {r.author_role !== "parent" && (
                          <span className="ml-2 text-[10px] tracking-[0.18em] uppercase text-[#8A9984]">
                            {r.author_role === "admin" ? "director" : "caregiver"}
                          </span>
                        )}
                      </span>
                      <span className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863]">
                        {timeAgo(r.created_at)}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-[#2C2B29] whitespace-pre-wrap">
                      {r.body}
                    </p>
                    {(isMe ||
                      (currentUser?.role &&
                        currentUser.role !== "parent")) && (
                      <button
                        type="button"
                        onClick={() => remove(r.id)}
                        data-testid={`thread-reply-${r.id}-remove`}
                        className="mt-2 text-[10px] tracking-[0.18em] uppercase text-[#6A6863] hover:text-[#2C2B29] underline"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                );
              })
            )}
            <div ref={endRef} />
          </div>
        )}

        {canReply && (
          <div className="mt-5 flex items-start gap-3">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              data-testid="thread-reply-input"
              rows={2}
              maxLength={2000}
              placeholder={
                mode === "quiet"
                  ? "Reply quietly to staff only…"
                  : "Reply gently…"
              }
              className="flex-1 bg-[#F2EFEA] rounded-2xl px-4 py-3 text-sm outline-none focus:bg-white border hairline resize-none"
            />
            <button
              type="button"
              onClick={send}
              disabled={busy || !body.trim()}
              data-testid="thread-reply-send"
              className="btn-primary !py-2 !px-4 !text-xs shrink-0"
            >
              <Send size={12} strokeWidth={1.5} />
              {busy ? "…" : "Reply"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
